import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { findIndex } from 'lodash';
import PropTypes, { any } from 'prop-types';

import InfoButton from '../../../InfoButton';
import Tip from '../../../Tip';
import {
    Input,
    ToggleSwitch,
    Select,
    CheckBox,
} from '../../../Form';
import EventQuestion from './EventQuestion';
import AddEventQuestionPopUp from '../../Popups/AddEventQuestionPopUp';
import Img from '../../../Img';

import { isPickleBallSportTypeEvent, getUsapDefaultQuestionIndex } from '../../../../utils/events';
import { isTournamentClub } from '../../../../utils/clubs';
import { passwordForDisplay, sportTypeIds, eventQuestionTypeIds, flexLeagueHomeCourtQuestion } from '../../../../utils/eventConstants';
import { useStatsig } from '../../../hooks/useStatsig';
import { ConfirmActionPopup } from '../../../PopUps';

const _ = { findIndex };

function AdditionalSettings(props) {
    const {
        formApi,
        isFlexLeagueEvent,
        clubTierType,
        clubTmsSmsFee,
        showClubJoin,
        clubLocation,
        playersRegistered,
        isTemplate,
        sportTypeId,
        isEditing,
    } = props;

    const coreUI = useSelector(({ coreUI }) => coreUI);
    const { values } = formApi;

    const [showAdditionalSettings, setShowAdditionalSettings] = useState(true);
    const [showEventQuestionsPopup, setShowEventQuestionsPopup] = useState(false);
    const [eventQuestionEditData, setEventQuestionEditData] = useState(null);
    const [smsChargeConfirmation, setSmsChargeConfirmation] = useState(null);
    const isPickleballEvent = isPickleBallSportTypeEvent(sportTypeId);

    const { checkGate, featureGates } = useStatsig();
    const isAllowPlayerPostingEnabled = checkGate(featureGates.EVENT_ALLOW_PLAYER_POSTING);

    const eventPasswordValidate = value => ({
        error: !value ? 'You must enter a password if you turn on the password feature on this event.' : null,
    });
    const { permissionTypes } = coreUI.types.tms || {};

    const isEventInUnitedStates = isTemplate || (clubLocation.countryName === 'United States' || clubLocation.countryCode3 === 'USA' || clubLocation.countryIoc === 'USA' || clubLocation.countryCode2 === 'US');
    const isEventPaid = parseInt(formApi.values.entryFee) > 0 || parseInt(formApi.values.eventDivisions?.reduce((total, division) => total + (division.entryFee || 0), 0)) > 0;
    const shouldShowSmsOptIn = isEventInUnitedStates && isEventPaid &&
        (isEditing ? (checkGate(featureGates.EVENTDESK_NOTIFICATIONS) || values.enableEventDeskNotifications) : checkGate(featureGates.EVENTDESK_NOTIFICATIONS));
    const tmsSmsFee = !isTemplate && (formApi.values.smsFee ?? clubTmsSmsFee).toFixed(2);
    const tmsSmsFeeEnabled = checkGate(featureGates.TMS_SMS_FEE);
    let smsOptInDescription = (
        <ul>
            <li>Send automated SMS texts for court assignments & match results.</li>
            <li>Works only with Order of Play or Next Available scheduling.</li>
            <li>Cannot be changed after first registration.</li>
        </ul>
    );
    let smsOptInInfoText = (
        <>
            <p className="text-info-heading">Enable SMS text messaging</p>
            <ui className="text-info-content">
                <li>If enabled, your event will have access to:</li>
                <ul className="ml16 mb0">
                    <li>UTR’s automated SMS text messaging system which sends players in-event messages for court assignments and match results.</li>
                    <li>Send custom SMS text messages to players in created draws within the event desk</li>
                </ul>
                <li>This feature is ONLY available for events using Order of Play or Next Available schedule types, which must be selected once in the event desk.</li>
                <li>Players will be informed during registration and can opt out if they prefer.</li>
                <li>This setting cannot be changed after the first player registers for the event</li>
            </ui>
        </>
    );
    if (tmsSmsFeeEnabled) {
        smsOptInDescription = (
            <ul>
                <li>Send custom SMS texts directly to players from the event desk.</li>
                {isPickleballEvent ? <li>Send automated SMS texts for <b>court assignments</b> and <b>match results</b>.<br />(Note: This feature is only available with <b>Next Available</b> schedule type.)</li> : null}
                <li>There is an <b>additional fee of ${tmsSmsFee} per unique player</b> (registrant) for this service.</li>
                <li>This setting <b>cannot be changed</b> once the first registration for the event has been completed.</li>
            </ul>
        );

        smsOptInInfoText = (
            <>
                <p className="text-info-heading">If enabled, your event will have access to:</p>
                <ui className="text-info-content">
                    <li>Send custom SMS texts directly to players from the event desk.</li>
                    {isPickleballEvent ? <li>Send automated SMS texts for <b>court assignments</b> and <b>match results</b>.<br />(Note: This feature is only available with <b>Next Available</b> schedule type.)</li> : null}
                    <li>There is an <b>additional fee of ${tmsSmsFee} per unique player</b> (registrant) for this service.</li>
                    <li>This setting <b>cannot be changed</b> once the first registration for the event has been completed.</li>
                    <li>Players will be informed during registration and can opt out if they prefer.</li>
                    <li>The SMS feature only supports US phone numbers.</li>
                </ui>
            </>
        );
    }

    const onClickAddEventQuestion = (e) => {
        e.preventDefault();
        setShowEventQuestionsPopup(true);
    };

    const onEditEventQuestion = (question, index) => {
        setEventQuestionEditData({
            question,
            index,
        });
        setShowEventQuestionsPopup(true);
    };

    const onEventQuestionPopupClose = () => {
        setEventQuestionEditData(null);
        setShowEventQuestionsPopup(false);
    };

    if (isTournamentClub(clubTierType)) {
        const privateMembersOnlyIndex = _.findIndex(permissionTypes, { value: 'private' });
        if (privateMembersOnlyIndex !== -1) {
            permissionTypes.splice(privateMembersOnlyIndex, 1);
        }
    }

    useEffect(() => {
        let defaultQuestion;
        if (isPickleballEvent && !isEditing) {
            defaultQuestion = {
                text: 'Please describe your Pickleball background and/or provide your UTPR (i.e. 3.0, 4.5, etc.) or equivalent rating',
                required: true,
                type: 1,
            };
        }
        const questions = values?.eventQuestions?.filter(val => val.type !== eventQuestionTypeIds.THIRDPARTY_ID);
        const usapDefaultQuestionIndex = getUsapDefaultQuestionIndex(values?.eventQuestions);
        if (isFlexLeagueEvent && checkGate(featureGates.FLEX_AUTO_DRAW_LOCATION)) {
            defaultQuestion = {
                text: flexLeagueHomeCourtQuestion,
                required: true,
                type: eventQuestionTypeIds.HOME_COURT,
            };
        }
        if (questions.length === 0 && defaultQuestion) {
            if (values.hasUsapVerificationTypeId && usapDefaultQuestionIndex !== -1) {
                const { eventQuestions } = values;
                eventQuestions.push(defaultQuestion);
                formApi.setValue('eventQuestions', eventQuestions);
            } else {
                formApi.setValue('eventQuestions', [defaultQuestion]);
            }
        }
        if (usapDefaultQuestionIndex !== -1 && usapDefaultQuestionIndex !== 0) {
            // Set the usap default question as top if it was not set to top
            const usapDefaultQuestion = values?.eventQuestions[usapDefaultQuestionIndex];
            questions.unshift(usapDefaultQuestion);
            formApi.setValue('eventQuestions', questions);
        }
    }, []);

    const onSmsOptInChange = (val) => {
        if (!val) {
            formApi.setValue('enableEventDeskNotifications', false);
        } else if (tmsSmsFeeEnabled) {
            setSmsChargeConfirmation(true);
        } else {
            formApi.setValue('enableEventDeskNotifications', true);
        }
    };

    return (
        <div className="col-12">
            <div
                className={`form-block-divider ${showAdditionalSettings ? 'is-open' : ''} clickable mb16`}
                onClick={() => {
                    setShowAdditionalSettings(!showAdditionalSettings);
                }}
                data-testid="event-edit.additional-settings-toggle"
            >
                <div className="title">
                    <div><label>Additional Details</label></div>
                    <small style={{ textTransform: 'unset' }}>
                        Community Membership, hide players, player registration questions & password...
                    </small>
                </div>
                <div className="click-area click-area-32">
                    <i className={`uts uts-${showAdditionalSettings ? 'collapse' : 'expand'}-arrow`} />
                </div>
            </div>
            {showAdditionalSettings && (
                <div className={`row`}>
                    <div className="col-12">
                        {(showClubJoin) && (
                            <ToggleSwitch
                                id={'eventClubJoinRequestType'}
                                formGroupCssClass={`mt16 mb0`}
                                label="Players become Club Members"
                                hoverText={
                                    <React.Fragment>Players who register for the event will also become members of your club, with an ability to opt out during event registration.</React.Fragment>
                                }
                                field="$clubJoin" // eventClubJoinRequestType - clubJoinRequestTypes
                                onChange={() => console.log(values)}
                                data-testid="event-edit.club-join-toggle"
                            />
                        )}
                        <ToggleSwitch
                            id={'playerVisibility'}
                            formGroupCssClass={`mt16`}
                            label="Hide registered players"
                            field="playerVisibility" // playerVisibility - bool
                            onChange={(val) => {
                                if (val) {
                                    formApi.setValue('needPartnerVisibility', false);
                                }
                            }}
                            hoverText={(
                                <ul>
                                    <li>This feature will only hide the names of registered players on division player lists from the public.</li>
                                    <li>Registration count totals will still be visible on the event page, within divisions and on searchable event cards across the UTR Sports platform, unless the &apos;Hide registration count&apos; toggle is also enabled.</li>
                                    <li>This feature can be toggled on or off at any time.</li>
                                </ul>
                            )}
                            reverse={true}
                            data-testid="event-edit.hide-players-toggle"
                        />
                        {!values.playerVisibility && (
                            <div className="pl16">
                                <div className="bl-black pl16">
                                    <ToggleSwitch
                                        id={'needPartnerVisibility'}
                                        formGroupCssClass={`mt16`}
                                        label={'Show players who need partners'}
                                        field="needPartnerVisibility"
                                        data-testid="event-edit.need-partner-visibility-toggle"
                                    />
                                </div>
                            </div>
                        )}
                        <ToggleSwitch
                            id={'playerCountVisibility'}
                            formGroupCssClass={`mt16`}
                            label={'Hide registration count'}
                            field="playerCountVisibility"
                            hoverText={(
                                <ul>
                                    <li>This feature will only hide the registration count totals displayed on the event page, division cards or searchable event cards across the UTR Sports platform.</li>
                                    <li>Names of registered players on division player lists will remain visible unless the &apos;Hide registered players&apos; setting is also enabled.</li>
                                    <li>This feature can be toggled on or off at any time.</li>
                                </ul>
                            )}
                            reverse={true}
                            data-testid="event-edit.hide-reg-count-toggle"
                        />
                        {isAllowPlayerPostingEnabled && !isFlexLeagueEvent && (
                            <ToggleSwitch
                                id={'allowPlayerPosting'}
                                formGroupCssClass={`mt16`}
                                label={`Allow Player Posting`}
                                field="allowPlayerPosting" // allowPlayerPosting - bool
                                onChange={() => console.log(values)}
                                data-testid="event-edit.allow-player-posting-toggle"
                            />
                        )}
                        <div className={`d-flex align-items-start`}>
                            <div className={`flex-1`}>
                                <div className={`form-group form-group-xl mt16`}>
                                    <label htmlFor={'verified'}>
                                        Event Visibility
                                        <InfoButton
                                            hoverText={(
                                                <ul>
                                                    <li>
                                                        Public events are listed in search results so anyone can search, find, view and register for the event.
                                                    </li>
                                                    <li>
                                                        Private with Link events are not listed in search results, but any player with the event link can view and register for the event.
                                                    </li>
                                                    <li>
                                                        Private - Members Only events are only available for your community members.
                                                    </li>
                                                </ul>
                                            )}
                                        />
                                    </label>
                                </div>
                            </div>
                            <div className={`flex-1`}>
                                <Select
                                    hiddenLabel={true}
                                    hiddenDefault={true}
                                    id={`eventPermissionType`}
                                    field={`eventPermissionType`}
                                    options={permissionTypes}
                                    disabled={playersRegistered}
                                    data-testid="event-edit.event-visibility-select"
                                />
                            </div>
                        </div>
                        {shouldShowSmsOptIn && (
                            <ToggleSwitch
                                id={'enableEventDeskNotifications'}
                                formGroupCssClass={`mt16`}
                                label={`Enable SMS text messaging`}
                                description={smsOptInDescription}
                                hoverText={smsOptInInfoText}
                                field="enableEventDeskNotifications"
                                disabled={isEditing && playersRegistered}
                                onChange={onSmsOptInChange}
                                data-testid="event-edit.sms-notifications-toggle"
                            />
                        )}
                        <Tip
                            variant="info"
                            dangerouslySetInnerHTML={true}
                            valign="top"
                            content="<b>Pro Tip:</b> Use our Additional Player Questions to create a great experience from the beginning by finding out more about your registrants. For example, in cases where players that register and are unrated, request details about their playing history so you can facilitate level-based play."
                            showTip={!isTemplate}
                        >

                            <div className="form-group form-group-xl">
                                <label>
                                    Additional Player Questions
                                </label>
                                <small>
                                    These questions will appear during player registration as optional or required. To view player responses and contact information, export the player list.
                                </small>
                                <div className="form-group form-group-xl mt16">
                                    {values.eventQuestions?.map((question, i) => {
                                        return (
                                            <EventQuestion
                                                key={`question${1}`}
                                                index={i}
                                                formApi={formApi}
                                                isTemplate={isTemplate}
                                                question={question}
                                                onEditQuestion={onEditEventQuestion}
                                                isFlexLeagueEvent={isFlexLeagueEvent}
                                            />
                                        );
                                    })}
                                </div>

                                {(!values.eventQuestions || !values.eventQuestions.length) &&
                                    <div className="text-small mb16">You haven&apos;t added any questions.</div>}

                                {values.eventQuestions && values.eventQuestions.length < 10 && (
                                    <div className="form-group form-group-xl">
                                        <a href="*" className="text-small" onClick={onClickAddEventQuestion} data-testid="event-edit.add-question-btn">
                                            <Img
                                                className="icon-16 rounded-icon-blue"
                                                src={'/img/v2/icons/icon-plus-blue-small.svg'}
                                            /> Add a question
                                        </a>
                                    </div>
                                )}

                                {!isTemplate && (
                                    <>
                                        <CheckBox
                                            id="passwordEnabled"
                                            field="passwordEnabled"
                                            label="Registration Password or PIN"
                                            description="Players will only be able to register for this event if they have the password or PIN you enter below."
                                            hoverText={(
                                                <ul>
                                                    <li>
                                                        This is a great tool to use when reopening registration for late players or creating different division pricing for groups of players (ex: club members vs walk-ins).
                                                    </li>
                                                </ul>
                                            )}
                                            customLabel={<span className={'text-medium text-weight-normal'}>Enable password or PIN for player registration</span>}
                                            formGroupCssClass="mb0"
                                            data-testid="event-edit.password-enabled"
                                        />
                                        {(values.passwordEnabled) && (
                                            <Input
                                                id="eventPassword"
                                                label={(
                                                    <span
                                                        className={'text-medium text-weight-normal'}
                                                    >
                                                        {values.passwordEnabled ? 'Reset password or pin in any combination of numbers and letters' : 'Enter password or pin in any combination of numbers and letters'}
                                                    </span>
                                                )}
                                                field="eventPassword"
                                                validate={values.passwordEnabled ? null : eventPasswordValidate}
                                                placeholder={values.passwordEnabled ? '***********' : '"Ex. GrandSlam4"'}
                                                disableProfanityCheck={true}
                                                onFocus={(e) => {
                                                    if (e.target.value === passwordForDisplay) {
                                                        e.target.value = null;
                                                    }
                                                }}
                                                onBlur={(e) => {
                                                    if (e.target.value === '') {
                                                        e.target.value = passwordForDisplay;
                                                    }
                                                }}
                                            />
                                        )}
                                    </>
                                )}
                            </div>
                        </Tip>
                    </div>
                </div>
            )}
            {showEventQuestionsPopup && (
                <AddEventQuestionPopUp
                    formApi={formApi}
                    isTemplate={isTemplate}
                    onCloseClick={onEventQuestionPopupClose}
                    editQuestionData={eventQuestionEditData}
                />
            )}
            {smsChargeConfirmation && (
                <ConfirmActionPopup
                    confirmationTitle="Confirm Per-Player Fee"
                    confirmationMessage={(
                        <>
                            You will be charged ${tmsSmsFee} for each player who registers for this event. Do you want to proceed?
                            <br /><br />
                            Please Note: Only US phone numbers are supported to receive SMS texts.
                        </>
                    )}
                    confirmButtonLabel="Confirm"
                    onCtaClick={() => {
                        formApi.setValue('enableEventDeskNotifications', true);
                        setSmsChargeConfirmation(false);
                    }}
                    onCloseClick={() => {
                        formApi.setValue('enableEventDeskNotifications', false);
                        setSmsChargeConfirmation(false);
                    }}
                    data-testid="modal.confirm-sms-fee"
                />
            )}
        </div>
    );
}

AdditionalSettings.propTypes = {
    formApi: PropTypes.objectOf(any).isRequired,
    isFlexLeagueEvent: PropTypes.bool,
    clubTierType: PropTypes.objectOf(any).isRequired,
    showClubJoin: PropTypes.bool,
    playersRegistered: PropTypes.bool,
    sportTypeId: PropTypes.number,
    isEditing: PropTypes.bool,
};

AdditionalSettings.defaultProps = {
    isFlexLeagueEvent: false,
    showClubJoin: false,
    playersRegistered: false,
    sportTypeId: sportTypeIds.TENNIS,
    isEditing: false,
};

export default AdditionalSettings;
